/**
 * Authentication Context for managing user login state
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'avyug_user_token';

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} name
 * @property {string} email
 * @property {string|null} phone
 * @property {string|null} avatar
 * @property {string} auth_provider
 * @property {string} created_at
 */

/**
 * @typedef {Object} AuthContextValue
 * @property {User|null} user
 * @property {boolean} loading - True while validating a stored session token.
 * @property {boolean} bootstrapped - True once the initial auth check has finished.
 * @property {boolean} isAuthenticated
 * @property {(email: string, password: string) => Promise<void>} login
 * @property {(name: string, email: string, password: string, phone?: string) => Promise<void>} signup
 * @property {(credential: string) => Promise<void>} googleLogin
 * @property {() => void} logout
 * @property {(data: Partial<User>) => Promise<void>} updateProfile
 * @property {() => Promise<void>} refreshProfile
 */

/**
 * Auth Provider component
 * @param {{ children: React.ReactNode }} props
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(() => !localStorage.getItem(TOKEN_KEY));
  const loading = !bootstrapped;

  // Validate stored token without blocking the rest of the UI shell.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setBootstrapped(true);
      return undefined;
    }

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    let cancelled = false;

    (async () => {
      try {
        const response = await api.get('/users/me');
        if (!cancelled) setUser(response.data);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        delete api.defaults.headers.common['Authorization'];
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/users/login', { email, password });
    const { token, user: userData } = response.data;
    
    localStorage.setItem(TOKEN_KEY, token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  }, []);

  const signup = useCallback(async (name, email, password, phone = null) => {
    const response = await api.post('/users/signup', { name, email, password, phone });
    // New signup flow returns requires_verification: true
    // No token is returned until email is verified
    return response.data;
  }, []);

  const setAuthFromResponse = useCallback((data) => {
    // Used after OTP verification to set the authenticated user
    const { token, user: userData } = data;
    if (token && userData) {
      localStorage.setItem(TOKEN_KEY, token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
    }
  }, []);

  const googleLogin = useCallback(async (credential) => {
    const response = await api.post('/users/google', { credential });
    const { token, user: userData } = response.data;
    
    localStorage.setItem(TOKEN_KEY, token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (data) => {
    const response = await api.patch('/users/me', data);
    setUser(response.data);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const response = await api.get('/users/me');
      setUser(response.data);
    } catch (err) {
      // Silent fail
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      bootstrapped,
      isAuthenticated: !!user,
      login,
      signup,
      googleLogin,
      logout,
      updateProfile,
      refreshProfile,
      setAuthFromResponse,
    }),
    [
      user,
      loading,
      bootstrapped,
      login,
      signup,
      googleLogin,
      logout,
      updateProfile,
      refreshProfile,
      setAuthFromResponse,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 * @returns {AuthContextValue}
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
